import type { FeedItem, FeedVacancyItem } from '@/entities/feed';

type FeedCardDetail = {
  label: string;
  value: string;
};

export type FeedCardModel = {
  id: string;
  type: FeedItem['type'];
  title: string;
  eyebrow: string;
  supportingText: string;
  owner: string;
  avatar: string | null;
  hasContacts: boolean;
  description: string | null;
  details: FeedCardDetail[];
  target: {
    kind: 'vacancy' | 'summary';
    id: string;
  };
};

export function toFeedCardModel(item: FeedItem): FeedCardModel {
  if (item.type === 'vacancy') {
    const vacancy = item.vacancy;

    return {
      id: item.id,
      type: item.type,
      title: vacancy.title,
      eyebrow: vacancy.company.name,
      // «от 60 000 до 72 000 ₽ · Томск»; remote work reads «Удалённо», a missing part leaves no separator.
      supportingText: [formatSalary(vacancy.salary.min, vacancy.salary.max, ''), vacancyLocation(vacancy)]
        .filter(Boolean)
        .join(' · '),
      owner: vacancy.company.name,
      avatar: vacancy.company.logo,
      hasContacts: vacancy.hasContacts,
      description: vacancy.description,
      details: [
        { label: 'Город', value: vacancy.city.title },
        { label: 'Опыт работы', value: vacancy.experience.title },
        { label: 'Желаемый график', value: vacancy.schedules.map(({ name }) => name).join(', ') || 'Не указан' },
        ...(vacancy.address ? [{ label: 'Адрес', value: vacancy.address }] : []),
      ],
      target: { kind: 'vacancy', id: item.id },
    };
  }

  const summary = item.summary;
  const fullName = [summary.candidate.name, summary.candidate.surname].filter(Boolean).join(' ');

  return {
    id: item.id,
    type: item.type,
    title: summary.title,
    eyebrow: `${fullName}, ${summary.age === null ? 'Возраст не указан' : `${summary.age} ${getYearLabel(summary.age)}`}, ${summary.city.title}`,
    supportingText: summary.professions.map(({ title }) => title).join(', '),
    owner: fullName,
    avatar: summary.candidate.photo,
    hasContacts: summary.hasContacts,
    description: null,
    details: [
      { label: 'Город', value: summary.city.title },
      { label: 'Возраст', value: summary.age === null ? 'Не указан' : `${summary.age} ${getYearLabel(summary.age)}` },
      { label: 'Профессии', value: summary.professions.map(({ title }) => title).join(', ') || 'Не указаны' },
      { label: 'Опыт в профессии', value: summary.experienceInProfession?.title ?? 'Не указан' },
      { label: 'Желаемый график', value: summary.schedules.map(({ name }) => name).join(', ') || 'Не указан' },
    ],
    target: { kind: 'summary', id: item.id },
  };
}

/** Schedule type 5 «Удаленная работа»: the city does not matter to the applicant. */
const REMOTE_SCHEDULE = 5;

function vacancyLocation(vacancy: FeedVacancyItem['vacancy']): string {
  if (vacancy.schedules.some(({ type }) => type === REMOTE_SCHEDULE)) return 'Удалённо';
  return vacancy.city?.title?.trim() ?? '';
}

function formatSalary(min: number | null, max: number | null, fallback = ''): string {
  const formatter = new Intl.NumberFormat('ru-RU');

  if (min !== null && max !== null) return `от ${formatter.format(min)} до ${formatter.format(max)} руб/мес`;
  if (min !== null) return `от ${formatter.format(min)} руб/мес`;
  if (max !== null) return `до ${formatter.format(max)} руб/мес`;

  return fallback;
}

function getYearLabel(value: number): string {
  const lastTwoDigits = value % 100;
  const lastDigit = value % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';

  return 'лет';
}
